import 'core-js/stable'
import 'regenerator-runtime/runtime'

import React, { Fragment, useCallback } from 'react'
import { ApolloProvider } from '@apollo/client'
import Head from 'next/head'
import { Auth0Provider } from '@auth0/auth0-react'
import fetch from 'node-fetch'
import { Link } from 'react-router-dom'
// @ts-ignore
import { abortableFetch } from 'abortcontroller-polyfill/dist/cjs-ponyfill'
import '@scentregroup/ui/fonts/index.css'
import '@scentregroup/ui/tools/reset.css'

import FeatureFlagsProvider from '@scentregroup/shared/components/feature-flags-provider'
import SkipToContentLink from '@scentregroup/shared/components/skip-to-content-link'

import {
  GetHrefContext,
  AgentInfoContext,
  CountryContext,
  BasicPageContext,
  LinkContext,
} from '@scentregroup/shared/context'
import { EvolvedContextProvider } from '@scentregroup/shared/evolved'
import { agentInfo } from '@scentregroup/shared/helpers/get-browser'
import {
  CONFIG_KEYS,
  getClientConfigValue,
  NATIONAL_CONFIG,
} from '@scentregroup/shared/config'
import RouteChangeIndicator from '@scentregroup/shared/hub-components/route-change-indicator'
import { Spinner } from '@scentregroup/ui'
import googleOptimizeUrl from '@scentregroup/shared/google-optimize-url'
import { useApollo } from '../lib/apolloClient'
import { useRouter } from '../lib/router'
import ScrollToTop from '../../../src/client/containers/scroll-to-top'
import NextQueryParamsProvider from '../lib/next-query-params-provider'

// See https://github.com/apollographql/apollo-client/issues/6765#issuecomment-734469527
global.fetch = abortableFetch(fetch).fetch

export interface IBasePageProps {
  isBasicPage?: boolean
}

export type IAppPageProps<P = unknown> = IBasePageProps & P

interface IAppProps<P> {
  Component: React.FunctionComponent<
    P & {
      err?: any
    }
  >
  pageProps: IAppPageProps<P>
  err?: any
}

const getAgent = () =>
  (typeof navigator !== 'undefined' && navigator.userAgent) || ''

export default function App({
  Component,
  pageProps: { isBasicPage, ...pageProps },
  err,
}: IAppProps<unknown>) {
  const apolloClient = useApollo()
  const { locale, asPath } = useRouter()

  const META_INFORMATION =
    locale === 'en-NZ' ? NATIONAL_CONFIG.nz : NATIONAL_CONFIG.au

  const getHref = useCallback(
    () =>
      (typeof window !== 'undefined' && window.location.href) ||
      `${META_INFORMATION.url}${asPath}`,
    [META_INFORMATION, asPath]
  )

  return (
    <Fragment>
      <Head>
        <link
          rel="icon"
          type="image/png"
          href="/static/media/favicon.png"
          sizes="32x32"
        />
        <link
          rel="apple-touch-icon"
          href="/static/media/apple-touch-icon.png"
          type="image/png"
        />
        <link rel="manifest" href="/static/media/favicon-manifest.json" />
        <link rel="preconnect" href="https://res.cloudinary.com" />
        <title>{META_INFORMATION.title}</title>
        {META_INFORMATION.meta.map(({ name, content }) => (
          <meta key={name} name={name} content={content} />
        ))}
        <script src={googleOptimizeUrl()} />
      </Head>
      <LinkContext.Provider value={Link}>
        <EvolvedContextProvider isEvolved>
          <FeatureFlagsProvider doGetFromEnv>
            <ApolloProvider
              // @ts-ignore
              client={apolloClient}
            >
              <Auth0Provider
                domain={getClientConfigValue(
                  CONFIG_KEYS.NEXT_PUBLIC_AUTH0_DOMAIN
                )}
                clientId={getClientConfigValue(
                  CONFIG_KEYS.NEXT_PUBLIC_AUTH0_CLIENT_ID
                )}
                redirectUri={
                  typeof window === 'undefined'
                    ? undefined
                    : `${window.location.origin}/account/callback`
                }
              >
                <ScrollToTop>
                  <GetHrefContext.Provider value={getHref}>
                    <AgentInfoContext.Provider value={agentInfo(getAgent())}>
                      <CountryContext.Provider value={META_INFORMATION.country}>
                        <NextQueryParamsProvider>
                          <BasicPageContext.Provider
                            value={Boolean(isBasicPage)}
                          >
                            <RouteChangeIndicator
                              fallback={() => (
                                <div style={{ width: '100%', height: '100%' }}>
                                  <Spinner type="large" isSeasonal={false} />
                                </div>
                              )}
                            >
                              <SkipToContentLink skipToId="main-content" />
                              {/* Workaround for https://github.com/vercel/next.js/issues/8592 */}
                              <Component {...pageProps} err={err} />
                            </RouteChangeIndicator>
                          </BasicPageContext.Provider>
                        </NextQueryParamsProvider>
                      </CountryContext.Provider>
                    </AgentInfoContext.Provider>
                  </GetHrefContext.Provider>
                </ScrollToTop>
              </Auth0Provider>
            </ApolloProvider>
          </FeatureFlagsProvider>
        </EvolvedContextProvider>
      </LinkContext.Provider>
    </Fragment>
  )
}
